// Session admin robuste : sur un 401 (token expiré après 12 h, onglet resté ouvert, etc.), re-connexion
// silencieuse avec les identifiants mémorisés puis rejeu de la requête. Si impossible → retour à l'écran
// de connexion /admin avec un message clair, au lieu d'une redirection muette.
import axios from "axios";
import { getSession, setSession, clearSession } from "./authStorage";

const API = process.env.REACT_APP_BACKEND_URL;
let refreshing = null;
let installed = false;

export const clearAdminSession = () => {
  ["adminToken", "adminLoggedIn", "adminPassword", "adminUsername"].forEach(clearSession);
  delete axios.defaults.headers.common["Authorization"];
};

export const silentRelogin = async () => {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const password = getSession("adminPassword");
    if (!password) return null;
    const username = (getSession("adminUsername") || "").trim();
    const r = await axios.post(`${API}/api/admin/login`, username ? { username, password } : { password }, { timeout: 20000, _skipAuthRetry: true });
    const token = r.data?.token;
    if (!token) return null;
    setSession("adminToken", token);
    setSession("adminLoggedIn", "true");
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    return token;
  })().catch(() => null).finally(() => { refreshing = null; });
  return refreshing;
};

export const installAdminAuthInterceptor = () => {
  if (installed) return;
  installed = true;
  const isBackendApi = (url) => typeof url === "string" && (url.startsWith(`${API}/api/`) || url.startsWith("/api/")) && !url.includes("/api/admin/login");
  // Jeton admin ajouté automatiquement à TOUS les appels vers notre API (axios et fetch) dès qu'une session admin existe.
  axios.interceptors.request.use((cfg) => {
    const token = getSession("adminToken");
    if (token && isBackendApi(cfg.url || "") && !cfg.headers?.Authorization && !cfg.headers?.authorization) {
      cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${token}` };
    }
    return cfg;
  });
  const nativeFetch = window.fetch.bind(window);
  const expireToLogin = () => {
    clearAdminSession();
    if (window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin") {
      window.location.assign("/admin?session=expired");
    }
  };
  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url || "";
    const token = getSession("adminToken");
    if (!(token && isBackendApi(url))) return nativeFetch(input, init);
    const withAuth = (tok) => {
      const headers = new Headers(init.headers || (typeof input !== "string" ? input.headers : undefined) || {});
      headers.set("Authorization", `Bearer ${tok}`);
      return nativeFetch(input, { ...init, headers });
    };
    const res = await withAuth(token);
    if (res.status !== 401) return res;
    // Jeton expiré : reconnexion silencieuse puis un seul rejeu ; sinon retour à l'écran de connexion
    const fresh = await silentRelogin();
    if (fresh) return withAuth(fresh);
    expireToLogin();
    return res;
  };
  axios.interceptors.response.use(undefined, async (error) => {
    const cfg = error?.config || {};
    const url = cfg.url || "";
    const isApi = url.includes("/api/") && !url.includes("/api/admin/login");
    if (error?.response?.status !== 401 || !isApi || cfg._skipAuthRetry || cfg._retried) throw error;
    if (!getSession("adminLoggedIn") && !getSession("adminToken") && !getSession("adminPassword")) throw error;
    const token = await silentRelogin();
    if (token) {
      cfg._retried = true;
      cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${token}` };
      return axios(cfg);
    }
    clearAdminSession();
    if (window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin") {
      window.location.assign("/admin?session=expired");
    }
    throw error;
  });
};
